import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Message } from '../types';
import { getMedia } from '../services/api';

interface MessageCardProps {
  message: Message;
  // When true, this bubble CONTINUES the previous message in the same speaker's
  // turn — hide the sender/channel/time header so a run of messages from one
  // person reads as a single turn instead of repeating the label on every line.
  grouped?: boolean;
}

type MediaRef = { id: number; content_type: string; page_index: number };

// One captured attachment. Lazily streams its bytes via the MSAL-authed
// getMedia (axios `api`, {background:true}) into an object URL. Images render as
// a small thumbnail that opens full-size in an overlay; PDFs (and any non-image
// type) render a document chip that opens the bytes in a new tab. The object URL
// is revoked on unmount to avoid leaks; a failed fetch shows a placeholder.
const MediaAttachment: React.FC<{ media: MediaRef }> = ({ media }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [full, setFull] = useState(false);
  const isImage = (media.content_type || '').toLowerCase().startsWith('image/');

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    getMedia(media.id)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [media.id]);

  if (failed) {
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-400">
        <span aria-hidden>⚠</span>
        <span>Attachment unavailable</span>
      </div>
    );
  }

  if (!url) {
    // Loading placeholder — matches the thumbnail footprint so the card doesn't jump.
    return <div className="h-24 w-24 animate-pulse rounded-lg bg-gray-100" aria-hidden />;
  }

  if (!isImage) {
    // PDF / non-image document — a chip that opens the bytes in a new tab.
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
      >
        <span aria-hidden>📄</span>
        <span>Open document</span>
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setFull(true)}
        className="block overflow-hidden rounded-lg border border-gray-200 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
        title="View full size"
      >
        <img
          src={url}
          alt="Attachment"
          className="h-24 w-24 object-cover"
          loading="lazy"
        />
      </button>
      {full && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setFull(false)}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={url}
            alt="Attachment, full size"
            className="max-h-full max-w-full rounded-lg object-contain shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </>
  );
};

const parseDate = (dateStr: string | Date | null | undefined): Date => {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return dateStr;
  let d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d;
  try {
    const parts = dateStr.match(/(\d+)\/(\d+)\/(\d+)\s+(\d+):(\d+):(\d+)\s*(AM|PM)?/i);
    if (parts) {
      let [, month, day, year, hours, minutes, seconds, ampm] = parts;
      let h = parseInt(hours, 10);
      if (ampm && ampm.toUpperCase() === 'PM' && h < 12) h += 12;
      if (ampm && ampm.toUpperCase() === 'AM' && h === 12) h = 0;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), h, parseInt(minutes), parseInt(seconds));
    }
  } catch (e) {}
  return new Date();
};

const formatTimeAgo = (dateStr: string | Date | null | undefined): string => {
  try {
    const date = parseDate(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${month}/${day} ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return '';
  }
};

// A single message bubble. Presentational only — tags, status (progress) and
// the detail/comments/reply actions all live on the sub-thread subject row now,
// not on individual messages. Direction is conveyed visually (inbound = white /
// left, outbound = indigo / right) rather than with a badge.
const MessageCard: React.FC<MessageCardProps> = ({ message, grouped = false }) => {
  const isOutbound = message.direction === 'outbound';

  // Who the bubble is from: for outbound, the person/system who SENT it
  // (Auto-reply or the rep); for inbound, the resolved contact or their number.
  const sender = isOutbound
    ? (message.sent_by || 'Sent')
    : (message.customer_name || message.sender_id);

  const renderBody = () => {
    if (message.channel === 'EMAIL' && message.raw_message?.startsWith('Subject:')) {
      const lines = message.raw_message.split('\n');
      const subjectLine = lines[0].replace('Subject:', '').trim();
      const body = lines.slice(2).join('\n').trim();
      return (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-1">{subjectLine}</p>
          <p className="text-gray-800 whitespace-pre-wrap">{body}</p>
        </div>
      );
    }
    return <p className="text-gray-800 whitespace-pre-wrap">{message.raw_message}</p>;
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${
      isOutbound ? 'bg-indigo-50/70 border-indigo-100' : 'bg-white border-gray-200'
    }`}>
      {!grouped && (
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className={`text-xs font-medium truncate ${isOutbound ? 'text-indigo-700' : 'text-gray-600'}`}>
            {sender}
          </span>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-gray-400">{formatTimeAgo(message.received_at)}</span>
          </span>
        </div>
      )}
      {renderBody()}
      {/* Captured attachments (Phase 6.2 image intake) — inline thumbnails /
          document chips, lazily streamed via the authed media endpoint. */}
      {!!message.media?.length && (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.media.map((m) => (
            <MediaAttachment key={m.id} media={m} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageCard;
